// Grouped review entries: several review items that ask one question render
// as one entry the reviewer decides in one action (ReviewItemGroupDrawer).
// Each member stays its own item on the wire, so every write, apply and
// lineage rule is the item's own; grouping is how the list reads.
//
//   - teams: the team names an award or modification states (Workstream
//     identity items), confirmed together, each editable or rejectable;
//   - same: one conflict a document repeats line by line (the same field,
//     reason and choices on several lines), settled with one value;
//   - silent: the award deliverables one SOW never mentions (SAD-969), owed
//     per the award schedule in one action, with per-deliverable exceptions;
//   - row: the questions one SOW row raises (which award deliverable it is,
//     its due timing, its handling), answered together, each its own choice
//     (SAD-972);
//   - repeat (M7): one question several SOWs of the set raise on the same
//     printed text (a boilerplate table every SOW carries), asked once; each
//     SOW's item takes the answer and keeps its own record and team.
import { ReviewReasonCode } from '@mono/proto-clients';
import { displayValue, fieldLabel, isOpenReviewItem, NO_TARGET_NAMES, picksTeam, STRUCTURED_VALUE, targetName } from './reviewItemCopy';
// A team-naming item: the stated name of a team the award or a modification
// funds (pangaea's award and modification naming). A SOW's "Which team?" and
// a product family's "Same team?" are their own questions, never confirmed in
// bulk with the names.
const OWN_TEAM_QUESTIONS = new Set([
    'sow_team',
    'family'
]);
function isOwnTeamQuestion(r) {
    return r.targetObjectType === 'Workstream' && OWN_TEAM_QUESTIONS.has(r.targetFieldPath.trim());
}
export function isTeamNaming(r) {
    return picksTeam(r) && r.targetObjectType === 'Workstream' && !isOwnTeamQuestion(r);
}
function sameKey(r) {
    if (r.candidates.length < 2) return '';
    const labels = r.candidates.map((c)=>c.label || c.key);
    return [
        r.targetObjectType,
        r.targetFieldPath,
        String(r.reasonCode),
        ...labels
    ].join('\u0001');
}
// isSowSilent: a SOW never mentions this award deliverable (SAD-969).
export function isSowSilent(r) {
    return r.reasonCode === ReviewReasonCode.SOW_SILENT_ON_DUTY;
}
// The document a SOW reading's item is from: its stamped name, else the
// document key robin's item key carries ("robin:sow:<doc>:<fact>…").
export function sowDocumentOf(r) {
    if (r.documentLabel.trim()) return r.documentLabel.trim();
    const m = /^robin:sow:([^:]+):/.exec(r.key);
    return m ? m[1] : '';
}
// rowKey (SAD-972): the SOW row a SOW reading's question is about — its
// document, page and passage. "" for an item that is not one robin raised on
// a row, or has no choice to make.
export function rowKey(r) {
    if (!r.key.startsWith('robin:sow:') || r.candidates.length === 0) return '';
    const src = r.sources[0];
    const quote = (src?.quote ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
    if (!quote || !src?.page) return '';
    return [
        sowDocumentOf(r),
        String(src.page),
        quote
    ].join('\u0001');
}
// A list marker that opens a passage ("2.", "(3)", "•").
const LIST_MARKER = /^\s*(?:\(?\d{1,2}[.)]|[•▪*–-])\s+/u;
// A row's own numbering cells: each SOW numbers the same row its own way.
const NUMBERING_CELLS = /\b(?:SOW Sub-Section|PWS Section|CLIN)\s*:\s*[^|]*\|?/giu;
// A printed PWS reference in parentheses ("(5.2.3.B)", "(PWS 5.4.4 A)").
const PAREN_REF = /\(\s*(?:PWS\s*)?\d+(?:\.\d+)+\.?\s?[A-Z]?\s*\)/gu;
// patternForm (R15-B): a passage as the question it poses reads it, whichever
// SOW prints it: the numbering each SOW gives the same row (its sub-section,
// PWS section and CLIN cells, a PWS reference in parentheses, a list marker)
// set aside, then whitespace, case and punctuation. Every word the SOW says
// about the deliverable stays: two passages worded differently never share a
// form.
export function patternForm(quote) {
    return quote.replace(/\s+/g, ' ').replace(LIST_MARKER, '').replace(NUMBERING_CELLS, ' ').replace(PAREN_REF, ' ').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
}
// titleOnly (R15-B): a passage that is only a deliverable's name ("Weekly
// Meeting"), with no cell, clause or sentence of its own. What a SOW does with
// it turns on the rest of that SOW, so the same name in two SOWs is no shared
// answer: on run 12, 5 of the 15 such groups were answered differently SOW by
// SOW.
export function titleOnly(quote) {
    const t = quote.replace(/\s+/g, ' ').replace(LIST_MARKER, '').trim();
    return !/\||:|[.;]\s|\b(?:shall|will|is|are|must)\b/u.test(t) && t.split(' ').length <= 8;
}
// repeatKey (M7, R15-B): the question a SOW reading asks of a passage,
// whichever SOW prints it: the target kind, field and fact it asks, and the
// passage's pattern form. "" for an item robin did not raise on a SOW
// passage, with no choice to make, or on a passage that is only a name.
export function repeatKey(r) {
    if (!r.key.startsWith('robin:sow:') || r.candidates.length < 2) return '';
    const raw = r.sources[0]?.quote ?? '';
    const quote = patternForm(raw);
    const fact = r.key.split(':')[3] ?? '';
    if (!quote || !fact || titleOnly(raw)) return '';
    return [
        r.targetObjectType,
        r.targetFieldPath,
        fact,
        quote
    ].join('\u0001');
}
// A choice that names a team (routed to another team, this one or a new
// one) means something different in each SOW, so it is answered SOW by SOW.
function namesTeam(c) {
    if (c.workstreamId) return true;
    try {
        const o = JSON.parse(c.valueJson);
        return !!o && typeof o === 'object' && Object.keys(o).some((k)=>k.includes('workstream') || k === 'new_team');
    } catch  {
        return false;
    }
}
// sharedChoices (M7): the choices every member offers (the same key and
// label) that mean the same in every SOW, in the first member's order. Each
// member is answered with its own candidate of that key.
export function sharedChoices(members) {
    const [first, ...rest] = members;
    if (!first) return [];
    const id = (c)=>`${c.key}\u0001${c.label}`;
    const offered = rest.map((m)=>new Set(m.review.candidates.map(id)));
    return first.review.candidates.filter((c)=>!namesTeam(c) && offered.every((o)=>o.has(id(c))));
}
// The SOWs a group's members are from, in member order.
export function groupDocuments(g) {
    return [
        ...new Set(g.members.map((m)=>sowDocumentOf(m.review)))
    ];
}
// The SOW a SOW item writes on: its change item's target object
// ({"assignment": "<sow id>"}).
export function sowOfItem(d) {
    try {
        const loc = JSON.parse(d.targetRecordLocatorJson || '{}');
        return loc.assignment ?? '';
    } catch  {
        return '';
    }
}
// groupReviewItems keeps the list's order: a group sits where its first
// member was. A would-be group of one stays a single item.
export function groupReviewItems(items) {
    // A SOW row with one question is no row group: it groups as before.
    const onRow = new Map();
    for (const d of items){
        const row = rowKey(d.review);
        if (row) onRow.set(row, (onRow.get(row) ?? 0) + 1);
    }
    const onOwnRow = (d)=>{
        const row = rowKey(d.review);
        return !!row && (onRow.get(row) ?? 0) > 1;
    };
    // A row that asks one question twice (the same fact) asks no several
    // questions: it may be asked across SOWs like any other.
    const rowFacts = new Map();
    for (const d of items){
        const row = rowKey(d.review);
        if (row) rowFacts.set(row, (rowFacts.get(row) ?? new Set()).add(d.review.key.split(':')[3] ?? ''));
    }
    const onQuestionsRow = (d)=>onOwnRow(d) && (rowFacts.get(rowKey(d.review))?.size ?? 0) > 1;
    // Text two or more SOWs print alike is asked once (M7) when its items share
    // at least two choices that mean the same in every SOW.
    const repeats = new Map();
    for (const d of items){
        const r = d.review;
        if (isOwnTeamQuestion(r) || isTeamNaming(r) || isSowSilent(r) || onQuestionsRow(d)) continue;
        const k = repeatKey(r);
        if (k) repeats.set(k, [
            ...repeats.get(k) ?? [],
            d
        ]);
    }
    const repeated = new Set();
    for (const [k, members] of repeats){
        const docs = new Set(members.map((m)=>sowDocumentOf(m.review)));
        if (docs.size > 1 && sharedChoices(members).length > 1) repeated.add(k);
    }
    const keyOf = (d)=>{
        if (isOwnTeamQuestion(d.review)) return ''; // one document's or one family's question
        if (isTeamNaming(d.review)) return 'teams';
        if (isSowSilent(d.review)) return `silent:${sowOfItem(d)}`;
        const rk = repeatKey(d.review);
        if (rk && repeated.has(rk) && !onQuestionsRow(d)) return `repeat:${rk}`;
        if (onOwnRow(d)) return `row:${rowKey(d.review)}`;
        const k = sameKey(d.review);
        return k ? `same:${k}` : '';
    };
    const byKey = new Map();
    for (const d of items){
        const k = keyOf(d);
        if (!k) continue;
        byKey.set(k, [
            ...byKey.get(k) ?? [],
            d
        ]);
    }
    const out = [];
    const placed = new Set();
    for (const d of items){
        const k = keyOf(d);
        const members = k ? byKey.get(k) ?? [] : [];
        if (members.length < 2) {
            out.push({
                kind: 'single',
                item: d
            });
            continue;
        }
        if (placed.has(k)) continue;
        placed.add(k);
        out.push({
            kind: 'group',
            group: {
                id: k,
                mode: k === 'teams' ? 'teams' : k.startsWith('silent:') ? 'silent' : k.startsWith('row:') ? 'row' : k.startsWith('repeat:') ? 'repeat' : 'same',
                members
            }
        });
    }
    return out;
}
export function isOpenEntry(e) {
    return e.kind === 'single' ? isOpenReviewItem(e.item) : e.group.members.some(isOpenReviewItem);
}
// The name a team item states: its new-team candidate's label.
export function statedTeamName(r) {
    const c = r.candidates.find((x)=>!x.workstreamId) ?? r.candidates[0];
    return c?.label ?? '';
}
// The line a member is on, for a same-value group's list: a deliverable by
// its title when the surface knows it (SAD-887).
export function memberLine(r, names = NO_TARGET_NAMES) {
    return targetName(r, names) || r.targetLocator.trim();
}
// What a group's members are: deliverables for a duty's rule or SOW
// assignment, else lines.
export function memberNoun(g, count = g.members.length) {
    if (g.mode === 'row') return count === 1 ? 'question' : 'questions';
    if (g.mode === 'repeat') return count === 1 ? 'SOW' : 'SOWs';
    const typ = g.members[0].review.targetObjectType;
    const one = typ === 'ContractDuty' || typ === 'DutyAssignment' ? 'deliverable' : 'line';
    return count === 1 ? one : `${one}s`;
}
// A choice as the reviewer reads it: its value, or its label when the value
// is a decision with no plain reading (a rule to keep or replace).
function choiceText(c, field) {
    const label = (c.label ?? '').trim();
    const o = (()=>{
        try {
            return JSON.parse(c.valueJson);
        } catch  {
            return null;
        }
    })();
    // Team / family answers hide the name in valueJson — prefer the label.
    if (label && o && (typeof o.workstream_id === 'string' || Array.isArray(o.workstream_ids) || o.new_team || typeof o.family === 'string')) {
        return label;
    }
    const v = displayValue(c.valueJson, field);
    return v && v !== STRUCTURED_VALUE ? v : label || v;
}
// A modification's rule conflict offers the rule on file and the
// modification's, labelled "On file: <rule>" / "Modification: <rule>".
const RULE_SIDE = /^(on file|modification):\s*/i;
// A group's title is short: the question, not the explanation (the drawer
// body carries the reason). Two choices read "<field>: A or B?"; more read
// "<field> on N lines: which value?". A rule conflict names the deliverable
// and both schedules in words (SAD-887).
export function groupTitle(g, names = NO_TARGET_NAMES) {
    const n = g.members.length;
    if (g.mode === 'teams') return `Confirm these ${n} teams`;
    if (g.mode === 'silent') return `The SOW doesn't mention ${n} ${memberNoun(g)}: owed?`;
    const r = g.members[0].review;
    if (g.mode === 'row') return `${n} ${memberNoun(g)} on the SOW row “${rowPassage(r, 80)}”`;
    if (g.mode === 'repeat') {
        const docs = groupDocuments(g).length;
        return `Asked once for ${docs} ${memberNoun(g, docs)}: “${rowPassage(r, 80)}”`;
    }
    const field = fieldLabel(r.targetFieldPath);
    const keys = r.candidates.map((c)=>c.key);
    if (r.targetObjectType === 'ContractDuty' && keys.includes('on_file') && keys.includes('modification')) {
        const rule = (key)=>(r.candidates.find((c)=>c.key === key)?.label ?? '').replace(RULE_SIDE, '').trim();
        const first = memberLine(r, names);
        const who = n > 1 ? `${first} and ${n - 1} more ${memberNoun(g, n - 1)}` : first;
        return `${who}: keep “${rule('on_file')}” or take the modification's “${rule('modification')}”?`;
    }
    if (r.candidates.length === 2) {
        const [a, b] = r.candidates.map((c)=>choiceText(c, r.targetFieldPath));
        return `${field}: ${a} or ${b}? (${n} ${memberNoun(g)})`;
    }
    return `${field} on ${n} ${memberNoun(g)}: which value?`;
}
// The row a row group is about, as printed (clipped to n).
export function rowPassage(r, n = 300) {
    const q = (r.sources[0]?.quote ?? '').replace(/\s+/g, ' ').trim();
    return q.length > n ? `${q.slice(0, n - 1)}…` : q;
}
export function sectionByDocument(items) {
    const order = [];
    const by = new Map();
    for (const d of items){
        const label = d.review.documentLabel.trim();
        if (!by.has(label)) {
            by.set(label, []);
            order.push(label);
        }
        by.get(label)?.push(d);
    }
    const labels = [
        ...by.has('') ? [
            ''
        ] : [],
        ...order.filter((l)=>l !== '')
    ];
    return labels.map((label)=>({
            label,
            items: by.get(label) ?? []
        }));
}
export function groupSummary(g, names = NO_TARGET_NAMES) {
    if (g.mode === 'row') {
        return g.members.map((m)=>m.review.reason.trim()).filter(Boolean).join(' · ');
    }
    if (g.mode === 'repeat') {
        const docs = groupDocuments(g);
        const more = docs.length > 3 ? ` and ${docs.length - 3} more` : '';
        return `${g.members[0].review.reason.trim()} · applies to ${docs.length} SOWs: ${docs.slice(0, 3).join(', ')}${more}`;
    }
    const listed = (g.mode === 'teams' ? g.members.map((m)=>statedTeamName(m.review)) : g.members.map((m)=>memberLine(m.review, names))).map((s)=>s.trim()).filter(Boolean);
    if (listed.length === 0) {
        // Never render ", , , and N more" when every member lacked a name.
        return `${g.members.length} ${memberNoun(g)}`;
    }
    const shown = listed.slice(0, 6).join(', ');
    return listed.length > 6 ? `${shown} and ${listed.length - 6} more` : shown;
}
